import { theme } from 'antd';
import { DeletedFilled } from 'assets/images/svg';
import { INTEGRATIONS_TYPES } from 'config/integrationTypes';
import { useManagePermittedRoles } from 'library/hooks';
import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectCredentialByID, selectCredentialLoading } from 'reduxStore/credentials/selectors';
import { credentialsActions } from 'reduxStore/credentials/slice';
import { Button, Divider, Flex, Modal, Space, Tooltip, Typography } from 'zui/Antd';

const { Title, Text } = Typography;

const DeleteCardModal = ({ children, credentialID, visible: visibleProp, setVisible: setVisibleProp }) => {
	const {
		token: { paddingLG, fontSizeSM, marginXL, marginXS, paddingXL, paddingXS, paddingSM }
	} = theme.useToken();

	const dispatch = useDispatch();

	// ✅ Redux state
	const credential = useSelector((state) => selectCredentialByID(state, credentialID));
	const isCredentialLoading = !!useSelector((state) => selectCredentialLoading(state, credentialID));

	// ✅ Local state
	const [internalVisible, setInternalVisible] = useState(false);
	const visible = visibleProp ?? internalVisible;
	const setVisible = setVisibleProp ?? setInternalVisible;

	// ✅ Permissions
	const canDeleteCredential =
		useManagePermittedRoles('deleteCredential') &&
		credential.integrationID !== INTEGRATIONS_TYPES.INTEGRATION_INNER_RANGE.id;

	const cardName = credential?.name || `#${credentialID}`;

	const handleCancel = useCallback(() => setVisible(false), [setVisible]);

	const handleDelete = () => {
		dispatch(credentialsActions.deleteCredential({ credentialID }));
		setTimeout(() => handleCancel(), 500);
	};

	return (
		<>
			<Tooltip
				title={!canDeleteCredential ? "You don't have permission to perform this action." : 'Delete Card'}
				placement='top'
			>
				<span onClick={() => canDeleteCredential && setVisible(true)}>
					{children ?? (
						<Button
							type='text'
							size='small'
							icon={<DeletedFilled color='currentColor' width={16} height={16} />}
							disabled={!canDeleteCredential}
						/>
					)}
				</span>
			</Tooltip>

			<Modal
				open={visible}
				onCancel={handleCancel}
				closeIcon={false}
				centered
				destroyOnClose
				footer={[
					<Flex
						align='center'
						justify='center'
						style={{ marginTop: marginXL, padding: `0px ${paddingXS}px` }}
						gap={12}
						key='footer'
					>
						<Button onClick={handleCancel} type='default' disabled={isCredentialLoading} block>
							Cancel
						</Button>
						<Button onClick={handleDelete} loading={isCredentialLoading} disabled={isCredentialLoading} type='primary' danger block>
							Delete Card
						</Button>
					</Flex>
				]}
				width={350}
				styles={{ content: { padding: paddingXL } }}
			>
				<Flex vertical align='center' justify='center' gap={paddingSM}>
					<DeletedFilled />
					<Flex vertical gap={paddingLG}>
						<Space
							size={paddingXS}
							direction='vertical'
							align='center'
							style={{ margin: `${paddingXS}px 0px` }}
						>
							<Title
								level={5}
								style={{
									margin: 0,
									lineHeight: '24px',
									textAlign: 'center',
									padding: `0px ${paddingXL}px`
								}}
							>
								Are you sure you want to delete this card?
							</Title>
							<Text
								style={{
									fontSize: fontSizeSM,
									lineHeight: '24px',
									textAlign: 'center',
									display: 'block'
								}}
							>
								This will remove the card from all <br /> assigned locks. Cannot be undone.
							</Text>
						</Space>

						<Flex vertical gap={marginXS}>
							<Divider orientation='center' plain style={{ margin: 0, padding: 0, marginTop: 0 }}>
								<Text style={{ fontSize: fontSizeSM, fontWeight: 200 }}>Selected Card</Text>
							</Divider>
							<Text style={{ fontSize: fontSizeSM, fontWeight: 200, textAlign: 'center' }}>
								{cardName}
							</Text>
						</Flex>
					</Flex>
				</Flex>
			</Modal>
		</>
	);
};

export default DeleteCardModal;
